import { t } from '../../i18n';
import { localizeItemName } from '../../i18n/content';
import type { MailboxDisplay, TradeGridItem } from './types';

export function parseTradeSideForReview(side: string, metadataByItemId?: Map<string, string>): TradeGridItem[] {
    const totals = new Map<string, number>();
    side
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
        .forEach((entry) => {
            const [itemIdRaw, qtyRaw] = entry.split(';').map((part) => part.trim());
            const itemId = itemIdRaw || '';
            const qty = Number.parseInt(qtyRaw || '0', 10);
            if (!itemId || !Number.isFinite(qty) || qty <= 0) return;
            totals.set(itemId, (totals.get(itemId) ?? 0) + qty);
        });

    return Array.from(totals.entries())
        .map(([itemId, quantity]) => {
            const metadata = metadataByItemId?.get(itemId);
            return {
                itemId,
                quantity,
                displayName: deriveDisplayName(itemId, metadata),
                icon: deriveIcon(metadata),
                metadata,
                tradeable: true,
            };
        })
        .sort((a, b) => a.itemId.localeCompare(b.itemId));
}

export function splitTradeForReview(trade: string): { myItems: TradeGridItem[]; theirItems: TradeGridItem[] } {
    const [offeredSide = '', askedSide = ''] = trade.split('|');
    return {
        myItems: parseTradeSideForReview(askedSide),
        theirItems: parseTradeSideForReview(offeredSide),
    };
}

export function formatTimestamp(raw: unknown): string {
    try {
        if (raw && typeof raw === 'object' && 'toDate' in raw && typeof (raw as { toDate?: unknown }).toDate === 'function') {
            const date = (raw as { toDate: () => Date }).toDate();
            return date.toLocaleString();
        }
        if (typeof raw === 'number' || typeof raw === 'string') {
            const date = new Date(Number(raw));
            if (!Number.isNaN(date.getTime())) {
                return date.toLocaleString();
            }
        }
    } catch {
        // Fall through to string conversion.
    }
    return String(raw ?? '');
}

export function isTradePresent(trade: string): boolean {
    return trade.trim().length > 0;
}

export function compactMessagePreview(text: string): string {
    const clean = text.trim();
    if (clean.length <= 28) {
        return clean || '-';
    }
    return `${clean.slice(0, 28)}...`;
}

export function getFirstTradeItem(trade: string): string {
    const [offeredSide = '', askedSide = ''] = trade.split('|');
    const firstPair = [offeredSide, askedSide]
        .flatMap((side) => side.split(',').map((entry) => entry.trim()))
        .find((entry) => entry.length > 0);
    if (!firstPair) {
        return t('userMenu.mailbox.tradeSummaryFallback');
    }
    const [itemId, qty] = firstPair.split(';').map((part) => part.trim());
    if (!itemId || !qty) {
        return t('userMenu.mailbox.tradeSummaryFallback');
    }
    return `${itemId} x${qty}`;
}

export function getThirdOption(mail: MailboxDisplay): string {
    if (isTradePresent(mail.trade)) {
        return getFirstTradeItem(mail.trade);
    }
    return compactMessagePreview(mail.msg);
}

export function moveItemBetweenBuckets(
    itemId: string,
    amount: number,
    from: TradeGridItem[],
    to: TradeGridItem[]
): void {
    const index = from.findIndex((item) => item.itemId === itemId);
    if (index < 0) return;
    const moved = from[index];
    const moveQty = Math.max(1, Math.min(amount, moved.quantity));
    moved.quantity -= moveQty;
    if (moved.quantity <= 0) {
        from.splice(index, 1);
    }

    const existing = to.find((item) => item.itemId === itemId);
    if (existing) {
        existing.quantity += moveQty;
        return;
    }
    to.push({ ...moved, quantity: moveQty });
    to.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function deriveDisplayName(itemId: string, metadata: Record<string, unknown> | string | undefined): string {
    return localizeItemName(itemId, metadata, itemId);
}

function deriveIcon(metadata: Record<string, unknown> | string | undefined): string {
    if (!metadata) return '📦';

    let source: Record<string, unknown> | undefined;
    if (typeof metadata === 'string') {
        try {
            source = JSON.parse(metadata) as Record<string, unknown>;
        } catch {
            return '📦';
        }
    } else {
        source = metadata;
    }

    const emoji = source.emoji;
    if (typeof emoji === 'string' && emoji.trim().length > 0) return emoji;
    return '📦';
}

function indexMetadataByItemId(rows: Array<{ itemId: string; metadataJson: string; tradeable?: boolean }>): Map<string, { metadataJson: string; tradeable: boolean }> {
    const byItem = new Map<string, { metadataJson: string; tradeable: boolean }>();
    rows.forEach((row) => {
        if (!byItem.has(row.itemId) && row.metadataJson) {
            byItem.set(row.itemId, { metadataJson: row.metadataJson, tradeable: row.tradeable !== false });
        }
    });
    return byItem;
}

export async function buildTradeReviewItems(
    mail: MailboxDisplay,
    perspective: 'incoming' | 'outgoing',
    fetchInventoryByPlayerId: (playerId: string) => Promise<Array<{ itemId: string; metadataJson: string; tradeable?: boolean }>>
): Promise<{ myItems: TradeGridItem[]; theirItems: TradeGridItem[] }> {
    const [myInventory, otherInventory] = await Promise.all([
        fetchInventoryByPlayerId(mail.toPlayerId),
        fetchInventoryByPlayerId(mail.fromPlayerId),
    ]);
    const myMetadata = indexMetadataByItemId(myInventory);
    const otherMetadata = indexMetadataByItemId(otherInventory);
    const [offeredSide = '', askedSide = ''] = mail.trade.split('|');

    if (perspective === 'incoming') {
        return {
            myItems: parseTradeSideForReview(askedSide, myMetadata),
            theirItems: parseTradeSideForReview(offeredSide, otherMetadata),
        };
    }

    return {
        myItems: parseTradeSideForReview(offeredSide, otherMetadata),
        theirItems: parseTradeSideForReview(askedSide, myMetadata),
    };
}

export function aggregateInventoryRows(
    rows: Array<{ itemId: string; quantity: number; metadata: Record<string, unknown> | string; tradeable?: boolean }>
): TradeGridItem[] {
    const byItem = new Map<string, TradeGridItem>();
    rows.forEach((row) => {
        const existing = byItem.get(row.itemId);
        const displayName = deriveDisplayName(row.itemId, row.metadata);
        if (existing) {
            existing.quantity += row.quantity;
        } else {
            byItem.set(row.itemId, {
                itemId: row.itemId,
                quantity: row.quantity,
                displayName,
                icon: deriveIcon(row.metadata),
                metadata: row.metadata,
                tradeable: row.tradeable !== false,
            });
        }
    });
    return Array.from(byItem.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function buildTradeCsv(myOfferGrid: TradeGridItem[], theirOfferGrid: TradeGridItem[]): string {
    const offered = myOfferGrid
        .filter((item) => item.quantity > 0)
        .map((item) => `${item.itemId};${item.quantity}`)
        .join(',');
    const asked = theirOfferGrid
        .filter((item) => item.quantity > 0)
        .map((item) => `${item.itemId};${item.quantity}`)
        .join(',');
    return `${offered}|${asked}`;
}
